import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/api/client';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
import AppShell from '@/components/layout/AppShell';
import HomeShell from '@/components/layout/HomeShell';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import UnclassifiedEmailsPage from '@/pages/UnclassifiedEmailsPage';
import SettingsPage from '@/pages/SettingsPage';
import CompaniesPage from './pages/CompaniesPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

const ProjectsListPage = lazy(() => import('@/pages/ProjectsListPage'));
const MaterialsLibraryPage = lazy(() => import('@/pages/MaterialsLibraryPage'));
const HardwareLibraryPage = lazy(() => import('@/pages/HardwareLibraryPage'));
const HardwareModelEditorPage = lazy(() => import('@/pages/HardwareModelEditorPage'));
const FinishesLibraryPage = lazy(() => import('@/pages/FinishesLibraryPage'));
const LaborRatesPage = lazy(() => import('@/pages/LaborRatesPage'));
const MarginProfilesPage = lazy(() => import('@/pages/MarginProfilesPage'));
const ConstructionMethodsPage = lazy(() => import('@/pages/ConstructionMethodsPage'));
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const FrameTemplatesPage = lazy(() => import('@/pages/FrameTemplatesPage'));
const AssemblyTemplatesPage = lazy(() => import('@/pages/AssemblyTemplatesPage'));
const ComponentTemplatesPage = lazy(() => import('@/pages/ComponentTemplatesPage'));
const CompanySettingsPage = lazy(() => import('@/pages/CompanySettingsPage'));
const ComplexityScoringPage = lazy(() => import('@/pages/ComplexityScoringPage'));
const UpholsteryDesignSettingsPage = lazy(() => import('@/pages/UpholsteryDesignSettingsPage'));
const PillowDesignsPage = lazy(() => import('@/pages/PillowDesignsPage'));
const PillowInsertsPage = lazy(() => import('@/pages/PillowInsertsPage'));
const OversizedRulesPage = lazy(() => import('@/pages/OversizedRulesPage'));
const FrameBuilderPage = lazy(() => import('@/pages/FrameBuilderPage'));
const DataManagementPage = lazy(() => import('@/pages/DataManagementPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage'));
const UserDetailPage = lazy(() => import('@/pages/admin/UserDetailPage'));
const RolesPage = lazy(() => import('@/pages/admin/RolesPage'));
const PermissionsPage = lazy(() => import('@/pages/admin/PermissionsPage'));
const ActivityLogsPage = lazy(() => import('@/pages/admin/ActivityLogsPage'));
const CncQueuePage = lazy(() => import('@/pages/CncQueuePage'));
const ManufacturingDashboardPage = lazy(() => import('@/pages/ManufacturingDashboardPage'));
const LogisticsDashboardPage = lazy(() => import('@/pages/LogisticsDashboardPage'));
const InventoryDashboardPage = lazy(() => import('@/pages/InventoryDashboardPage'));

export default function App() {
  // Refresh the cached profile (role/permissions) on load so role changes —
  // e.g. being promoted to Admin — reflect without a manual re-login.
  useEffect(() => {
    if (!useAuthStore.getState().accessToken) return;
    apiClient
      .get('/auth/me')
      .then(({ data }) => useAuthStore.getState().setUser(data))
      .catch(() => { /* 401s are handled by the response interceptor */ });
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/kx7q" element={<ResetPasswordPage />} />

      {/* Protected full-screen routes (no AppShell) */}
      <Route element={<ProtectedRoute />}>
        <Route path="/settings/frame-templates/:id/builder" element={<Suspense fallback={null}><FrameBuilderPage /></Suspense>} />
        <Route path="/change-password" element={<Suspense fallback={null}><ChangePasswordPage /></Suspense>} />
      </Route>

      {/* Protected routes wrapped in AppShell */}
      <Route element={<ProtectedRoute />}>
        <Route element={<HomeShell />}>
          <Route path="/" element={<Suspense fallback={null}><CalendarPage /></Suspense>} />
        </Route>
        <Route element={<AppShell />}>
          <Route path="/estimation" element={<DashboardPage />} />
          <Route path="/calendar" element={<Navigate to="/" replace />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/admin/unclassified-emails" element={<UnclassifiedEmailsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/projects" element={<Suspense fallback={null}><ProjectsListPage /></Suspense>} />
          <Route path="/settings/materials" element={<Suspense fallback={null}><MaterialsLibraryPage /></Suspense>} />
          <Route path="/settings/hardware" element={<Suspense fallback={null}><HardwareLibraryPage /></Suspense>} />
          <Route path="/settings/hardware-model-editor" element={<Suspense fallback={null}><HardwareModelEditorPage /></Suspense>} />
          <Route path="/settings/finishes" element={<Suspense fallback={null}><FinishesLibraryPage /></Suspense>} />
          <Route path="/settings/labor-rates" element={<Suspense fallback={null}><LaborRatesPage /></Suspense>} />
          <Route path="/settings/margins" element={<Suspense fallback={null}><MarginProfilesPage /></Suspense>} />
          <Route path="/settings/construction-methods" element={<Suspense fallback={null}><ConstructionMethodsPage /></Suspense>} />
          <Route path="/settings/products" element={<Suspense fallback={null}><ProductsPage /></Suspense>} />
          <Route path="/settings/frame-templates" element={<Suspense fallback={null}><FrameTemplatesPage /></Suspense>} />
          <Route path="/settings/assembly-templates" element={<Suspense fallback={null}><AssemblyTemplatesPage /></Suspense>} />
          <Route path="/settings/component-templates" element={<Suspense fallback={null}><ComponentTemplatesPage /></Suspense>} />
          <Route path="/settings/company" element={<Suspense fallback={null}><CompanySettingsPage /></Suspense>} />
          <Route path="/settings/complexity" element={<Suspense fallback={null}><ComplexityScoringPage /></Suspense>} />
          <Route path="/settings/upholstery" element={<Suspense fallback={null}><UpholsteryDesignSettingsPage /></Suspense>} />
          <Route path="/settings/pillow-designs" element={<Suspense fallback={null}><PillowDesignsPage /></Suspense>} />
          <Route path="/settings/pillow-inserts" element={<Suspense fallback={null}><PillowInsertsPage /></Suspense>} />
          <Route path="/settings/oversized" element={<Suspense fallback={null}><OversizedRulesPage /></Suspense>} />
          <Route path="/settings/data-management" element={<Suspense fallback={null}><DataManagementPage /></Suspense>} />
          <Route path="/admin/users" element={<Suspense fallback={null}><UserManagementPage /></Suspense>} />
          <Route path="/admin/users/:id" element={<Suspense fallback={null}><UserDetailPage /></Suspense>} />
          <Route path="/admin/roles" element={<Suspense fallback={null}><RolesPage /></Suspense>} />
          <Route path="/admin/permissions" element={<Suspense fallback={null}><PermissionsPage /></Suspense>} />
          <Route path="/admin/activity-logs" element={<Suspense fallback={null}><ActivityLogsPage /></Suspense>} />
          <Route path="/cnc-queue" element={<Suspense fallback={null}><CncQueuePage /></Suspense>} />
          <Route path="/manufacturing" element={<Suspense fallback={null}><ManufacturingDashboardPage /></Suspense>} />
          <Route path="/logistics" element={<Suspense fallback={null}><LogisticsDashboardPage /></Suspense>} />
          <Route path="/inventory" element={<Suspense fallback={null}><InventoryDashboardPage /></Suspense>} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
